import * as React from "react";
import { Text, View } from "react-native";
import { usePathname, useRouter } from "expo-router";
import { decodeEntity } from "html-entities";
import { FileIcon, FolderPlusIcon, HashIcon } from "lucide-react-native";

import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { useHackyParams } from "~/lib/constants";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import {
  decodeFilterFromURL,
  encodeFilterForURL,
  filterSchema,
  FilterSchema,
  SAVE_TYPE_OPTIONS,
} from "./params";

export function Filters() {
  const params = useHackyParams<{ filters: string }>();
  const filters = decodeFilterFromURL(params.filters);
  const { account, space } = useCurrentSpace();
  const toRender: Array<React.ReactElement> = [];
  const pathname = usePathname();
  const { push } = useRouter();

  const filterOptions: Array<{
    key: FilterSchema["collection"]["mode"];
    title: string;
  }> = [
    {
      key: "is",
      title: "is",
    },
    {
      key: "is_not",
      title: "is not",
    },

    {
      key: "is_any_of",
      title: "is any of",
    },
    {
      key: "is_all_of",
      title: "is all of",
    },
    {
      key: "is_none_of",
      title: "is none of",
    },
  ];

  if (!filters) {
    return null;
  }

  if (
    filters.collection.ids.length === 0 &&
    filters.tag.ids.length === 0 &&
    !filters.saveType
  ) {
    return null;
  }

  function getFilterOptions(type: keyof FilterSchema) {
    if (!type) return [];
    if (!filters) return [];

    const length = filters[type]?.ids.length ?? 0;

    if (length === 1) {
      return filterOptions
        .filter((option) => option.key === "is" || option.key === "is_not")
        .map((x) => ({
          ...x,
          onSelect() {
            push({
              pathname:
                "/(app)/(authenticated)/[account]/(space)/[space]/saves",
              params: {
                account: account.slug,
                space: space.slug,
                filters: encodeFilterForURL({
                  ...filters,
                  [type]: {
                    ...filters[type],
                    mode: x.key,
                  },
                }),
              },
            });
          },
        }));
    }

    return filterOptions
      .filter((option) => {
        if (type === "saveType")
          return option.key === "is_any_of" || option.key === "is_none_of";

        return (
          option.key === "is_any_of" ||
          option.key === "is_none_of" ||
          option.key === "is_all_of"
        );
      })
      .map((x) => ({
        ...x,
        onSelect() {
          push({
            pathname: "/(app)/(authenticated)/[account]/(space)/[space]/saves",
            params: {
              account: account.slug,
              space: space.slug,
              filters: encodeFilterForURL({
                ...filters,
                [type]: {
                  ...filters[type],
                  mode: x.key,
                },
              }),
            },
          });
        },
      }));
  }

  if (filters.saveType && filters.saveType?.ids.length > 0) {
    toRender.push(
      <View key="saveType" className="flex-row items-center gap-1">
        <FileIcon className="text-text-primary" size={16} />
        <Text className="text-text-primary">Type</Text>

        <Dropdown
          asChild
          trigger={
            <Button variant="outline" size="small">
              <Text className="text-text-primary">
                {filters.saveType.mode.replaceAll("_", " ")}
              </Text>
            </Button>
          }
          groups={[
            {
              items: getFilterOptions("saveType"),
            },
          ]}
        />
        {(() => {
          return (
            <Text className="text-text-primary">
              {filters.saveType.ids
                .map((type) => {
                  return SAVE_TYPE_OPTIONS.find((x) => x.type === type)?.title;
                })
                .join(", ")}
            </Text>
          );
        })()}
      </View>,
    );
  }

  if (filters.collection.ids.length > 0) {
    const collectionIdsLength = filters.collection.ids.length;

    toRender.push(
      <View key="collection" className="flex-row items-center gap-1">
        <FolderPlusIcon className="text-text-primary" size={16} />
        <Text className="text-text-primary">Collection</Text>

        <Dropdown
          asChild
          trigger={
            <Button variant="outline" size="small">
              <Text className="text-text-primary">
                {filters.collection.mode.replaceAll("_", " ")}
              </Text>
            </Button>
          }
          groups={[
            {
              items: getFilterOptions("collection"),
            },
          ]}
        />
        {(() => {
          if (collectionIdsLength === 1) {
            return (
              <Text className="text-text-primary">
                {
                  space.collectionById(filters.collection.ids.at(0) ?? "")
                    ?.title
                }
              </Text>
            );
          }
          if (collectionIdsLength > 1) {
            return (
              <View className="flex-row items-center gap-1">
                <Text className="text-text-primary">
                  {
                    space.collectionById(filters.collection.ids.at(0) ?? "")
                      ?.title
                  }
                </Text>
                <Text className="text-text-primary">
                  +{collectionIdsLength - 1} more
                </Text>
              </View>
            );
          }
          return null;
        })()}
      </View>,
    );
  }

  if (filters.tag.ids.length > 0) {
    const tagIdsLength = filters.tag.ids.length;

    toRender.push(
      <View key="tag" className="flex-row items-center gap-1">
        <HashIcon className="text-text-primary" size={16} />
        <Text className="text-text-primary">Tag</Text>

        <Dropdown
          asChild
          trigger={
            <Button variant="outline" size="small">
              <Text className="text-text-primary">
                {filters.tag.mode.replaceAll("_", " ")}
              </Text>
            </Button>
          }
          groups={[
            {
              items: getFilterOptions("tag"),
            },
          ]}
        />
        {(() => {
          if (tagIdsLength === 1) {
            return (
              <Text className="text-text-primary">
                {space.tagById(filters.tag.ids.at(0) ?? "")?.value}
              </Text>
            );
          }
          if (tagIdsLength > 1) {
            return (
              <View className="flex-row items-center gap-1">
                <Text className="text-text-primary">
                  {space.tagById(filters.tag.ids.at(0) ?? "")?.value}
                </Text>
                <Text className="text-text-primary">
                  +{tagIdsLength - 1} more
                </Text>
              </View>
            );
          }
          return null;
        })()}
      </View>,
    );
  }

  return (
    <View className="flex-row items-center gap-2">
      {toRender.map((x, i) => {
        if (i === toRender.length - 1) return x;
        return (
          <React.Fragment key={`divider-${i}`}>
            {x}
            <Text className="text-text-secondary">{decodeEntity("&amp;")}</Text>
          </React.Fragment>
        );
      })}
    </View>
  );
}
